import { Component } from '@theme/component';
import { replaceSection, sectionRenderer } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig, populateDiscountApplyButton } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  static observedAttributes = ['data-auto-discount-ready'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #autoDiscountRequestId = 0;

  connectedCallback() {
    super.connectedCallback?.();
    this.#bindFloatingLabel();
    this.#scheduleAutoDiscount();
    CartDiscount.#ensureApplyTriggerListener();
  }

  updatedCallback() {
    super.updatedCallback?.();
    this.#scheduleAutoDiscount();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'data-auto-discount-ready' || oldValue === newValue || !this.isConnected) return;
    this.#scheduleAutoDiscount();
  }

  #autoDiscountStorageKey(code) {
    return `cart-auto-discount-dismissed:${String(code).trim().toLowerCase()}`;
  }

  #wasAutoDiscountDismissed(code) {
    try {
      return sessionStorage.getItem(this.#autoDiscountStorageKey(code)) === '1';
    } catch {
      return false;
    }
  }

  #setAutoDiscountDismissed(code, dismissed) {
    try {
      const key = this.#autoDiscountStorageKey(code);
      if (dismissed) sessionStorage.setItem(key, '1');
      else sessionStorage.removeItem(key);
    } catch {
      return undefined;
    }
  }

  #scheduleAutoDiscount() {
    const code = String(this.dataset.autoDiscountCode || '').trim();
    if (!code) return;

    if (this.dataset.autoDiscountReady !== 'true') {
      this.#setAutoDiscountDismissed(code, false);
      return;
    }

    const codeMatches = (value) => String(value).trim().toLowerCase() === code.toLowerCase();
    if (this.#existingDiscounts().some(codeMatches)) return;
    if (this.#wasAutoDiscountDismissed(code)) return;

    const requestId = ++this.#autoDiscountRequestId;
    requestAnimationFrame(() => {
      if (requestId !== this.#autoDiscountRequestId || !this.isConnected) return;
      if (this.#existingDiscounts().some(codeMatches)) return;

      const form = this.querySelector('.cart__entry-field');
      const input = form?.querySelector('input[name="discount"]');
      if (form instanceof HTMLFormElement && input instanceof HTMLInputElement && !input.readOnly) {
        input.value = code;
        form.classList.add('has-value');
      }

      this.#applyDiscountCode(code, { silent: true });
    });
  }

  #bindFloatingLabel() {
    const form = this.querySelector('.cart__entry-field');
    const input = form?.querySelector('input[name="discount"]');
    const btn = form?.querySelector('.cart__entry-btn');
    if (!form || !(input instanceof HTMLInputElement)) return;

    const updateHasValue = () => {
      const hasVal = input.value.trim().length > 0 || input.readOnly;
      form.classList.toggle('has-value', !!hasVal);
    };

    const switchToEditMode = () => {
      if (!input.readOnly) return;
      input.readOnly = false;
      input.removeAttribute('readonly');
      input.placeholder = 'Enter promocode';
      if (btn) {
        btn.classList.remove('cart__entry-btn--applied', 'cart__entry-btn--loading');
        btn.removeAttribute('data-discount-code');
        btn.removeAttribute('on:click');
        btn.type = 'submit';
        btn.setAttribute('aria-label', 'Apply discount');
        populateDiscountApplyButton(btn);
      }
      updateHasValue();
    };

    form.addEventListener('focusin', () => {
      form.classList.add('has-focus');
      if (input.readOnly) switchToEditMode();
    });
    form.addEventListener('focusout', () => {
      form.classList.remove('has-focus');
      updateHasValue();
    });
    input.addEventListener('input', updateHasValue);
    input.addEventListener('focus', () => { if (input.readOnly) switchToEditMode(); });
    updateHasValue();
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement)) return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;

    await this.#applyDiscountCode(discountCodeValue, { event, silent: false });
  };

  async #applyDiscountCode(discountCodeValue, options = {}) {
    const silent = options.silent === true;
    const event = options.event;
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    if (typeof this.dataset.sectionId !== 'string') return;

    const form = this.querySelector('.cart__entry-field');
    const discountCode = form?.querySelector('input[name="discount"]');
    const submitBtn = form?.querySelector('.cart__entry-btn--newsletter[type="submit"]');
    const canEditInput = discountCode instanceof HTMLInputElement && !discountCode.readOnly;

    const codeMatches = (a, b) => String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
    const existingDiscounts = this.#existingDiscounts();
    if (existingDiscounts.some((c) => codeMatches(c, discountCodeValue))) return;

    const abortController = this.#createAbortController();

    if (submitBtn) {
      submitBtn.classList.add('cart__entry-btn--loading');
      submitBtn.disabled = true;
    }
    if (canEditInput) discountCode.disabled = true;
    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');

    try {
      const sectionId = this.dataset.sectionId;
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: sectionId,
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      const notApplicable = data.discount_codes?.find((/** @type {{ code: string; applicable: boolean; }} */ d) => codeMatches(d.code, discountCodeValue) && d.applicable === false);
      if (notApplicable) {
        if (canEditInput) {
          discountCode.value = '';
          discountCode.disabled = false;
          form?.classList.remove('has-value');
        }
        if (submitBtn) {
          submitBtn.classList.remove('cart__entry-btn--loading');
          submitBtn.disabled = false;
        }
        if (!silent) this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = sectionId && data.sections?.[sectionId];
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      await this.#refreshDiscountSection(sectionId, newHtml);
      if (typeof navigator.vibrate === 'function') navigator.vibrate(10);
    } catch (error) {
      if (submitBtn) {
        submitBtn.classList.remove('cart__entry-btn--loading');
        submitBtn.disabled = false;
      }
      if (canEditInput) discountCode.disabled = false;
    } finally {
      this.#activeFetch = null;
      if (event) cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  }

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    const clearBtn = event.target.closest('.cart__discount-clear');
    const entryBtn = event.target.closest('.cart__entry-btn--applied');
    const source = pill || clearBtn || entryBtn;
    if (!(source instanceof HTMLElement)) return;

    const discountCode = source.dataset.discountCode;
    if (!discountCode) return;

    const autoCode = String(this.dataset.autoDiscountCode || '').trim();
    if (autoCode && String(discountCode).trim().toLowerCase() === autoCode.toLowerCase()) {
      this.#setAutoDiscountDismissed(autoCode, true);
    }

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const form = this.querySelector('.cart__entry-field');
    const input = form?.querySelector('input[name="discount"]');
    const applyBtn = this.querySelector('.cart__entry-btn--newsletter, .cart__entry-btn');

    if (input instanceof HTMLInputElement) {
      input.value = '';
      input.readOnly = false;
      input.placeholder = 'Enter promocode';
      input.removeAttribute('readonly');
    }
    const clearEl = this.querySelector('.cart__discount-clear');
    if (clearEl) clearEl.remove();
    if (applyBtn) {
      applyBtn.classList.remove('cart__entry-btn--applied', 'cart__entry-btn--loading');
      applyBtn.removeAttribute('data-discount-code');
      applyBtn.removeAttribute('on:click');
      applyBtn.type = 'submit';
      applyBtn.setAttribute('aria-label', 'Apply discount');
      populateDiscountApplyButton(applyBtn);
    }
    if (form) form.classList.remove('has-value');

    this.#setDiscountProgressCodeApplied(false);

    const abortController = this.#createAbortController();

    try {
      const sectionId = this.dataset.sectionId;
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: sectionId }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (existingDiscounts.length === 0) {
        localStorage.removeItem('discount_code');
        document.cookie = 'discount_code=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      const newHtml = sectionId && data.sections?.[sectionId];
      await this.#refreshDiscountSection(sectionId, newHtml);
    } catch (error) {
      const sectionId = this.dataset.sectionId;
      if (sectionId) await this.#refreshDiscountSection(sectionId);
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  async #refreshDiscountSection(sectionId, html) {
    if (html) {
      try {
        replaceSection(sectionId, html);
        return;
      } catch {
      }
    }
    await sectionRenderer.renderSection(sectionId, { cache: false, replace: true });
  }

  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const pills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of pills) {
      if (pill instanceof HTMLElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }
    const clearBtn = this.querySelector('.cart__discount-clear');
    if (clearBtn instanceof HTMLElement && typeof clearBtn.dataset.discountCode === 'string') {
      discountCodes.push(clearBtn.dataset.discountCode);
    }
    const input = this.querySelector('input[name="discount"]');
    if (input instanceof HTMLInputElement && input.value && input.readOnly) {
      const v = input.value.trim();
      if (v && !discountCodes.includes(v)) discountCodes.push(v);
    }
    return discountCodes;
  }

  focusDiscountField() {
    const input = document.getElementById('cart-discount');
    if (!(input instanceof HTMLInputElement)) return;
    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    input.focus({ preventScroll: true });
  }

  retryAutoDiscount() {
    const autoCode = String(this.dataset.autoDiscountCode || '').trim();
    if (!autoCode) return;
    this.#setAutoDiscountDismissed(autoCode, false);
    this.#scheduleAutoDiscount();
  }

  static #applyTriggerListenerInit = false;

  static #ensureApplyTriggerListener() {
    if (CartDiscount.#applyTriggerListenerInit) return;
    CartDiscount.#applyTriggerListenerInit = true;
    document.addEventListener('click', (event) => {
      const trigger = event.target instanceof Element ? event.target.closest('[data-discount-apply-trigger]') : null;
      if (!trigger) return;
      event.preventDefault();
      const component = document.querySelector('cart-discount-component');
      if (!(component instanceof CartDiscount)) return;
      component.focusDiscountField();
      component.retryAutoDiscount();
    });
  }

  #setDiscountProgressCodeApplied(applied) {
    document.querySelectorAll('[data-cart-discount-progress]').forEach((root) => {
      if (!(root instanceof HTMLElement)) return;
      if (root.dataset.thresholdReached !== 'true') return;

      const pending = root.querySelector('[data-discount-progress-pending]');
      const apply = root.querySelector('[data-discount-progress-apply]');
      const unlocked = root.querySelector('[data-discount-progress-unlocked]');

      root.dataset.discountCodeApplied = applied ? 'true' : 'false';
      root.classList.toggle('cart-discount-progress--unlocked', applied);
      root.classList.toggle('cart-discount-progress--ready', !applied);

      if (pending) pending.hidden = true;
      if (apply) apply.hidden = applied;
      if (unlocked) unlocked.hidden = !applied;
    });
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
